'use client';

import { cn } from '../utils/cn';
import type {
  TreeSelectionOption,
  TreeSelectionValue,
} from '../types/TreeOptionType';

type ReadOnlySelectMultiTreeOptionsProps = {
  data: TreeSelectionOption[];
  value?: Record<string, TreeSelectionValue>;
  className?: string;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  // Translation function for validation messages
  t?: (
    key: string,
    options?: { count?: number; plural?: string; plural_verb?: string }
  ) => string;
} & React.HTMLAttributes<HTMLDivElement>;

// SVG Components for read-only display
const ReadOnlyCircleIcon = ({ isSelected }: { isSelected: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="block"
  >
    <rect
      x="0.5"
      y="0.5"
      width="15"
      height="15"
      rx="7.5"
      stroke="#D9D9D9"
      strokeWidth="1"
    ></rect>
    <circle cx="8" cy="8" r="6" fill={isSelected ? '#34BBEB' : ''}></circle>
  </svg>
);

export default function ReadOnlySelectMultiTreeOptions({
  data,
  value = {},
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  t,
  ...rest
}: ReadOnlySelectMultiTreeOptionsProps) {
  return (
    <>
      {/* All categories and their subtypes displayed together */}
      <div className="flex flex-col gap-2.5">
        {data.map((category) => {
          const categoryKey = category.key;
          const categorySelection = value[categoryKey];

          // For categories with single behavior, check if any children are selected
          const hasSelectedChildren =
            category.childrenBehavior === 'single' && category.subChoice
              ? category.subChoice.some((child) => {
                  const childKey = `${categoryKey}.${child.key}`;
                  return value[childKey]?.isChecked || false;
                })
              : false;

          const isCategorySelected =
            categorySelection?.isChecked || hasSelectedChildren;
          const categoryTextValue = categorySelection?.textValue || '';

          return (
            <div key={category.key}>
              {/* Category with optional checkbox */}
              <div className="">
                {category.childrenBehavior === 'single' ||
                !category.subChoice ||
                category.subChoice.length === 0 ? (
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center">
                      {/* Read-only SVG checkbox */}
                      <div className="mr-2 flex-shrink-0">
                        <ReadOnlyCircleIcon isSelected={isCategorySelected} />
                      </div>

                      <span
                        className={cn(
                          'text-14 font-medium',
                          isCategorySelected
                            ? 'text-el-grey-900'
                            : 'text-el-grey-500'
                        )}
                      >
                        {category.label}
                      </span>
                    </div>

                    {/* Text field for category if it has one and is selected */}
                    {isCategorySelected &&
                      category.hasTextField &&
                      categoryTextValue && (
                        <div className="ml-6">
                          <div className="text-12 text-el-grey-700 bg-el-grey-50 px-3 py-2 rounded border border-el-grey-300">
                            {categoryTextValue}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <span className="text-14 font-medium text-el-grey-800">
                    {category.label}
                  </span>
                )}
              </div>

              {/* Subtypes as radio buttons (single behavior) or checkboxes */}
              {category.subChoice && category.subChoice.length > 0 && (
                <div className="flex flex-col gap-1.5 ml-6 mt-1">
                  {category.subChoice.map((subtype) => {
                    const subtypeKey = `${category.key}.${subtype.key}`;
                    const currentSelection = value[subtypeKey];
                    const isSelected = currentSelection?.isChecked || false;
                    const textValue = currentSelection?.textValue || '';

                    return (
                      <div key={subtypeKey} className="flex flex-col gap-2">
                        <div className="flex items-center">
                          {/* Read-only SVG radio button or checkbox */}
                          <div className="mr-2 flex-shrink-0">
                            <ReadOnlyCircleIcon isSelected={isSelected} />
                          </div>

                          <span
                            className={cn(
                              'text-14',
                              isSelected
                                ? 'text-el-grey-900'
                                : 'text-el-grey-500'
                            )}
                          >
                            {subtype.label}
                          </span>
                        </div>

                        {/* Text field for this subtype if it has one and is selected */}
                        {isSelected && subtype.hasTextField && textValue && (
                          <div className="ml-6 relative -mt-1">
                            <div className="text-12 text-el-grey-700 bg-el-grey-50 px-3 py-2 rounded border">
                              {textValue}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
